import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Clock, User, Calendar, X } from "lucide-react";
import api from "@/lib/api";
import { PageHero } from "@/components/PageHero";
import { DataStamp, Disclaimer } from "@/components/Shared";

export default function ResearchDetail() {
  const { slug } = useParams();
  const [report, setReport] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [related, setRelated] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setLightbox(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    api.get(`/research/${slug}`).then((r) => setReport(r.data)).catch(() => setNotFound(true));
    api.get("/research").then((r) => setRelated(r.data.filter((x) => x.slug !== slug).slice(0, 3)));
  }, [slug]);

  if (notFound) return <div className="py-32 text-center text-slate-400">Report not found. <Link to="/research" className="text-navy underline">Back to research</Link></div>;
  if (!report) return <div className="py-32 text-center text-slate-400">Loading…</div>;

  const date = new Date(report.published_date).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });

  return (
    <div data-testid="research-detail-page">
      <PageHero
        eyebrow={report.category}
        title={report.title}
        crumbs={[{ label: "Research", to: "/research" }, { label: report.title }]}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {report.featured_image && (
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}${report.featured_image}`}
            alt={report.title}
            data-testid="research-featured-image"
            onClick={(e) => setLightbox(e.currentTarget.src)}
            className="w-full h-auto rounded-xl border border-border mb-8 shadow-sm cursor-zoom-in"
          />
        )}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-500 pb-6 border-b border-border">
          <span className="inline-flex items-center gap-1.5"><User className="h-4 w-4 text-gold" />{report.author}</span>
          <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4 text-gold" />{date}</span>
          <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4 text-gold" />{report.reading_time}</span>
        </div>

        <div className="mt-6"><DataStamp source={report.data_source} asOf={report.data_as_of} kind="Educational/Derived Analysis" /></div>

        {report.content_html ? (
          <div
            className="research-html mt-10"
            data-testid="research-html"
            onClick={(e) => { if (e.target.tagName === "IMG") setLightbox(e.target.src); }}
            dangerouslySetInnerHTML={{ __html: report.content_html }}
          />
        ) : (
          <>
            <article className="mt-10 space-y-5">
              {report.content.map((p, i) => (
                <p key={i} className="text-slate-600 leading-relaxed text-[15px]">{p}</p>
              ))}
            </article>

            <div className="mt-12 grid sm:grid-cols-2 gap-4">
              <div className="border border-border rounded-lg p-5">
                <h4 className="font-display font-semibold text-navy mb-2">Methodology</h4>
                <p className="text-sm text-slate-600 leading-relaxed">{report.methodology}</p>
              </div>
              <div className="border border-border rounded-lg p-5">
                <h4 className="font-display font-semibold text-navy mb-2">Risk Notes</h4>
                <p className="text-sm text-slate-600 leading-relaxed">{report.risk_notes}</p>
              </div>
            </div>
          </>
        )}

        <div className="mt-8"><Disclaimer /></div>

        {related.length > 0 && (
          <div className="mt-14">
            <h3 className="font-display text-2xl font-bold text-navy mb-6">Related Research</h3>
            <div className="space-y-3">
              {related.map((r) => (
                <Link key={r.slug} to={`/research/${r.slug}`} className="flex items-center justify-between p-4 border border-border rounded-md hover:bg-subtle transition-colors" data-testid={`related-${r.slug}`}>
                  <span className="text-sm font-medium text-navy">{r.title}</span>
                  <span className="text-xs text-slate-400">{r.category}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {lightbox && (
        <div
          data-testid="chart-lightbox"
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-navy/90 backdrop-blur-sm p-4 cursor-zoom-out"
        >
          <button
            data-testid="chart-lightbox-close"
            onClick={() => setLightbox(null)}
            className="absolute top-5 right-5 text-white/70 hover:text-white transition-colors"
            aria-label="Close"
          >
            <X className="h-7 w-7" />
          </button>
          <img src={lightbox} alt="Full size" className="max-h-[92vh] max-w-[92vw] object-contain rounded-lg shadow-2xl" />
        </div>
      )}
    </div>
  );
}
